import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { useReportsMetaSelector } from "store";
import type { CardConfig, KpiCardConfig } from "store/types";

import { SectionLabel } from "./SectionLabel";

interface Props {
  config: KpiCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const KpiSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { KPIFields, KPICharacteristics } = useReportsMetaSelector();

  const kpiOptions = useMemo(() => [...KPIFields, ...KPICharacteristics], [KPIFields, KPICharacteristics]);

  const metricsFromConfig = useMemo(() => {
    if (!config.cubics) return [];
    return Object.values(config.cubics).map((c) => ({
      title: c.title ?? "",
      filter: c.filter ?? ""
    }));
  }, [config.cubics]);

  const [draftMetrics, setDraftMetrics] = useState(metricsFromConfig);

  const isHydratingRef = useRef(true);

  useEffect(() => {
    if (isHydratingRef.current) {
      setDraftMetrics(metricsFromConfig);
      isHydratingRef.current = false;
      return;
    }

    const same = JSON.stringify(metricsFromConfig) === JSON.stringify(draftMetrics);

    if (!same) {
      setDraftMetrics(metricsFromConfig);
    }
  }, [metricsFromConfig]);

  const syncToConfig = useCallback(
    (next: { title: string; filter: string }[]) => {
      const valid = next.filter((m) => m.title.trim() !== "" && m.filter.trim() !== "");

      const cubics: Record<string, { title: string; filter: string }> = {};

      valid.forEach((m, i) => {
        cubics[`cubic${i + 1}`] = m;
      });

      const nextConfig = {
        ...config,
        cubics: valid.length ? cubics : undefined
      };

      const same = JSON.stringify(config.cubics) === JSON.stringify(nextConfig.cubics);

      if (!same) {
        onUpdateConfig(nextConfig);
      }
    },
    [config, onUpdateConfig]
  );

  const handleAdd = useCallback(() => {
    setDraftMetrics((prev) => {
      if (prev.length >= 5) return prev;
      return [...prev, { title: "", filter: "" }];
    });
  }, []);

  const handleRemove = useCallback((index: number) => {
    setDraftMetrics((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdate = useCallback((index: number, field: "title" | "filter", value: string) => {
    setDraftMetrics((prev) => {
      const updated = [...prev];
      const current = updated[index] ?? { title: "", filter: "" };

      updated[index] = {
        ...current,
        [field]: value
      };

      if (field === "filter" && (!current.title || current.title === current.filter)) {
        updated[index].title = value;
      }

      return updated;
    });
  }, []);

  useEffect(() => {
    syncToConfig(draftMetrics);
  }, [draftMetrics, syncToConfig]);

  return (
    <Box>
      <SectionLabel>KPI Metrics</SectionLabel>

      {draftMetrics.length === 0 && (
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", mb: 1 }}>
          No metrics configured. Add metrics to fetch data from the API.
        </Typography>
      )}

      {draftMetrics.map((metric, index) => (
        <Box key={index} sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#9CA3AF", flex: 1 }}>
              Metric {index + 1}
            </Typography>

            <IconButton
              size="sm"
              onClick={() => handleRemove(index)}
              sx={{ p: "2px" }}
              disabled={draftMetrics.length <= 1}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          <TextField
            size="small"
            placeholder="Title"
            value={metric.title}
            onChange={(e) => handleUpdate(index, "title", e.target.value)}
            sx={{
              "& .MuiInputBase-input": { fontSize: 12, p: "6px 8px" }
            }}
          />

          <SelectAutocomplete<string>
            options={kpiOptions}
            defaultValue={kpiOptions.includes(metric.filter) ? metric.filter : null}
            onChange={(value) => {
              if (value) handleUpdate(index, "filter", value);
            }}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Value"
                size="small"
                sx={{
                  "& .MuiInputBase-input": { fontSize: 12 }
                }}
              />
            )}
          />
        </Box>
      ))}

      <Button
        size="sm"
        disabled={draftMetrics.length >= 5}
        onClick={handleAdd}
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        sx={{
          fontSize: 11,
          textTransform: "none",
          color: "#26A6AE",
          p: "4px 8px"
        }}
      >
        Add Metric
      </Button>
    </Box>
  );
};
